import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {PearlHarbor} from "../../PearlHarbor";
import {PearlHarborGameMode} from "../../server/PearlHarborGameMode";
import {IDogFight, IFinalWave} from "../../server/PearlHarborData";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {PearlHarborModel} from "../../server/PearlHarborModel";
import {BonusMeters} from "./BonusMeters";
import {PearlHarborSoundManager} from "../../sound/PearlHarborSoundManager";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {AnimatedMeshRope} from "../../components/AnimatedMeshRope";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {PearlHarborSoundConfig} from "../../sound/PearlHarborSoundConfig";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export interface IMeter {
    meter:PIXI.Sprite,
    spinAnimation:PIXI.spine.Spine,
    text:PIXI.Text,
    flyText:PIXI.Text,
    mode:string,
    isActive:boolean,
    isMulti:boolean,
    value:number,
    hasWon:boolean,
    hasBurnt:boolean,
    hasBurnt2:boolean,
    swooshHolder:PIXI.Container,
    swooshContainer:PIXI.Container
}

export interface IPanel {
    panel:PIXI.Sprite,
    name:string,
    text:PIXI.Text
}

export class CocpitBoard extends PIXI.Container implements StageLayer {

    private VALUE_COLLECTOR_POS:number[] = [720, -200];
    private FS_METER_POS:number[][] = [
        //[0, 300], [0, 600], [0, 900],
        [154, 136], [230, 924], [230, 1060],
        [384, 788], [384, 924], [384, 1060],
        [518, 788], [518, 924], [518, 1060],
        [496, 788], [496, 924], [496, 1060],
        [550, 788], [550, 924], [550, 1060],
        [594, 788], [594, 924], [594, 1060]
    ];
    private FS_METER_STATE_COLOR:string[] = ["red", "#7a2e2e", "#8d6c3f"];
    private readonly METER_ANIMATION:any = {
        spin : "spin",
        stop0 : "Stop",
        stop1 : "Stop2",
        stop2 : "Stop2",
        waiting : "waiting",
        win : "meter3WinAttention",
        multiplierHit : "multiplier_hit",
        plusHit : "plus_hit",
        payout : "payout",
        spread : "spread",
        receiving : "receiving",
        columnAttention : "column_win_attention",
        rowAttention : "row_win_attention",
        columnWin : "column_win",
        rowWin : "row_win",
        burningSpent : "state_burning_spent",
        burning : "state_burning",
        cracked : "state_cracked"
    };
    private FLY_TEXT_STYLE:PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily : "Arial",
        "dropShadowColor" : "#ddc5c5",
        "fill" : "red",
        "fontSize" : 90,
        "fontWeight" : "bold",
        "letterSpacing" : 1,
        "lineJoin" : "round",
        "stroke" : "white",
        "strokeThickness" : 4
    });

    public static readonly TOTAL_MULTIPLIER:PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily : "Arial",
        "dropShadow" : true,
        "dropShadowAngle" : 1.8,
        "dropShadowBlur" : 12,
        "dropShadowDistance" : 12,
        "fill" : "red",
        "fillGradientStops" : [1],
        "fontSize" : 130,
        "fontWeight" : "bold",
        "letterSpacing" : 1,
        "lineJoin" : "round",
        "stroke" : "white",
        "strokeThickness" : 5
    });

    private readonly MAINGAME_MULTI_STYLE:PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily : "Arial",
        fontWeight : "bold",
        fill : ["#ffffff", "#ff0000"],
        fontSize : "84px",
        strokeThickness : 4,
        stroke : "#453028"
    });
    private readonly ROUND_MULTIPLIER:PIXI.TextStyle = new PIXI.TextStyle({
        fontFamily : "Arial",
        "dropShadow" : true,
        "dropShadowAngle" : 1.8,
        "dropShadowBlur" : 12,
        "dropShadowDistance" : 12,
        "fill" : "red",
        "fillGradientStops" : [1],
        "fontSize" : 130,
        "fontWeight" : "bold",
        "letterSpacing" : 1,
        "lineJoin" : "round",
        "stroke" : "white",
        "strokeThickness" : 5
    });
    private readonly WIN_NUMBER_FLY_TO_MEDDLE:number = 0.7;
    private readonly WIN_NUMBER_SHOW_IN_MEDDLE:number = 0.5;

    private _fsMeters:IMeter[];
    private _fsContainer:PIXI.Container;
    private _swooshContainer:PIXI.Container;
    private _sfsContainer:PIXI.Container;
    private _sfsEnhanceRow:PIXI.Sprite;
    private _multiplier:IPanel;
    private _roundWinMultiplier:PIXI.Text;
    private _mainGameMulti:PIXI.Text;
    private _mainMultiSwooshContainer:PIXI.Container;
    private _currentRoundWin:number;
    private _currentRoundWinText:IPanel;
    private _columnSums:number[];
    private _rowSums:number[];
    private _columnHits:number[];
    private _rowHit:number[];
    private _totalMulti:number;
    private _hasPlusLanded:boolean = false;
    private _bonusMeters:BonusMeters;
    private _addEffects:PIXI.spine.Spine;

    public get totalMulText():string {
        return this._multiplier.text.text;
    }

    public setTextStyle(meter:IMeter, burnt:boolean = false):void {
        if(meter.isActive) {
            meter.text.style.fill = this.FS_METER_STATE_COLOR[0];
            return;
        }

        if((meter.hasBurnt || !this.isSFS()) && burnt) {
            meter.text.style.fill = this.FS_METER_STATE_COLOR[2];
            meter.text.alpha = 0.4;
            return;
        }

        if((meter.hasBurnt && !this.isSFS())) {
            meter.text.style.fill = this.FS_METER_STATE_COLOR[2];
            meter.text.alpha = 0.4;
            return;
        }

        if((meter.hasBurnt && meter.hasBurnt2 && this.isSFS())) {
            meter.text.style.fill = this.FS_METER_STATE_COLOR[2];
            meter.text.alpha = 0.4;
            return;
        }

        meter.text.style.fill = this.FS_METER_STATE_COLOR[1];
    }

    private createPanelSprite(spriteName:string, pos:number[], style:PIXI.TextStyle, string:string):IPanel {
        const panel:PIXI.Sprite = new PIXI.Sprite();
        panel.name = spriteName;
        panel.anchor.set(0.5, 0.5);
        this.addChild(panel);
        const text:PIXI.Text = new PIXI.Text(string, style);

        panel.addChild(text);
        text.anchor = new PIXI.Point(0.5, 0.5);
        text.position.set(0, 0);
        panel.position.set(pos[0], pos[1]);
        return {
            panel : panel,
            name : spriteName,
            text : text
        };
    }

    private addMeters(pos:number[][], container:PIXI.Container, isSFS:boolean):IMeter[] {
        let meters:IMeter[] = [];
        const meterContainer = new PIXI.Container();
        container.addChild(meterContainer);

        pos.forEach((meterPos:number[], i:number) => {
            const meter:PIXI.Sprite = new PIXI.Sprite();
            meter.name = `meter${i}_${Math.floor(i / 3)}${i % 3}`;
            meter.anchor.set(0.5, 0.5);
            meter.scale.set(0.9, 0.9);
            meter.position.set(pos[0][0] * Math.floor(i / 3), pos[0][1] * (i % 3) - 30);
            meterContainer.addChild(meter);

            const spineName:string = ((isSFS && (i % 3 === 1)) ? "sfsMeter" : "meter");
            const spinAnimation = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset(spineName));
            spinAnimation.state.setAnimation(0, this.METER_ANIMATION.spin, true);
            spinAnimation.position.set(0, 0);
            const text = new PIXI.Text("", {
                fontFamily : "Arial",
                "dropShadowColor" : "#ddc5c5",
                "fill" : "red",
                "fillGradientStops" : [
                    1
                ],
                "fontSize" : 90,
                "fontWeight" : "bold",
                "letterSpacing" : 1,
                "lineJoin" : "round",
                "stroke" : "white",
                "strokeThickness" : 4
            });
            text.position.set(0, ((isSFS && (i % 3 === 1)) ? 19 : 0));
            text.anchor.set(0.5, 0.5);
            text.visible = false;
            meter.addChild(spinAnimation, text);

            const swooshHolder = new PIXI.Container();
            swooshHolder.name = `metersign${i}_${Math.floor(i / 3)}${i % 3}`;
            swooshHolder.visible = false;
            swooshHolder.position.set(meter.x, meter.y);
            const swooshContainer = new PIXI.Container();
            swooshHolder.addChild(swooshContainer);
            const flyText = new PIXI.Text("", this.FLY_TEXT_STYLE);
            flyText.anchor.set(0.5);
            flyText.position.set(0, 0);
            swooshHolder.addChild(flyText);
            this._swooshContainer.addChild(swooshHolder);

            meters.push({
                meter : meter,
                hasWon : false,
                isActive : true,
                spinAnimation : spinAnimation,
                text : text,
                isMulti : false,
                hasBurnt : false,
                hasBurnt2 : false,
                value : 0,
                mode : "FS",
                swooshHolder : swooshHolder,
                swooshContainer : swooshContainer,
                flyText : flyText
            })
        });
        return meters;
    }

    public reset():void {
        this.visible = false;
        this._mainGameMulti.position.set(386, -490);
        this._bonusMeters.reset();
        this._fsContainer.visible = false;
        this._fsMeters = [];
        this._fsContainer.removeChildren();
        this._addEffects.state.setEmptyAnimation(0, 0);
    }

    private init():void {
        this.scale.set(0.6, 0.6);
        this.visible = false;
        this._hasPlusLanded = false;
        this._rowHit = ArrayHelper.initArrayWithValues(3, () => 0);
        this._columnHits = ArrayHelper.initArrayWithValues(6, () => 0);
    }

    private isSFS():boolean {
        return PearlHarbor.model.data.mode === PearlHarborGameMode.SUPER_FREESPIN;
    }

    constructor() {
        super();
        this.name = "CocpitBoard";
        this._bonusMeters = new BonusMeters();
        this.addChild(this._bonusMeters);

        this._fsContainer = new PIXI.Container();
        this._sfsContainer = new PIXI.Container();
        this.position.set(131, 486);
        this.addChild(this._sfsContainer);
        this.addChild(this._fsContainer);
        this._fsContainer.visible = false;
        this._sfsContainer.visible = false;

        this._sfsEnhanceRow = new PIXI.Sprite(PearlHarbor.assetLoader.getTextures("dashBoardPiceForToraSpins")[0]);
        this._sfsEnhanceRow.name = "sfsEnhanceRowBg";
        this._sfsEnhanceRow.anchor.set(0.5, 0.5);
        this._sfsEnhanceRow.position.set(386, 103);
        this._sfsContainer.addChild(this._sfsEnhanceRow);

        this._roundWinMultiplier = new PIXI.Text("x", CocpitBoard.TOTAL_MULTIPLIER);
        this.addChild(this._roundWinMultiplier);
        this._roundWinMultiplier.anchor = new PIXI.Point(0.5, 0.5);
        this._roundWinMultiplier.position.set(360, 360);
        this._roundWinMultiplier.visible = false;

        this._mainMultiSwooshContainer = new PIXI.Container();
        this.addChild(this._mainMultiSwooshContainer);

        this._mainGameMulti = new PIXI.Text("", CocpitBoard.TOTAL_MULTIPLIER);
        this.addChild(this._mainGameMulti);
        this._mainGameMulti.anchor = new PIXI.Point(0.5, 0.5);
        this._mainGameMulti.position.set(388, -510);
        this._mainGameMulti.visible = false;

        this._multiplier = this.createPanelSprite("", [70, -186], CocpitBoard.TOTAL_MULTIPLIER, "X");

        this._addEffects = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset("fsAdd"));
        this._addEffects.position.set(700, -186);
        this._addEffects.scale.set(1.67);
        this._addEffects.visible = false;
        this.addChild(this._addEffects);
        this._currentRoundWinText = this.createPanelSprite("", [700, -186], this.ROUND_MULTIPLIER, "");

        this._swooshContainer = new PIXI.Container();
        this._swooshContainer.visible = false;
        this.addChild(this._swooshContainer);
        this.init();
    }

    private spinMeter(meter:IMeter):void {
        if(meter.hasWon) {
            return;
        }
        meter.spinAnimation.state.setAnimation(0, this.METER_ANIMATION.spin, true);
        meter.meter.visible = true;
        meter.text.visible = (meter.text.text.length > 0);
    }

    private stopMeter(meter:IMeter, i:number):void {
        if(meter.hasWon) {
            return;
        }
        meter.meter.texture = PearlHarbor.assetLoader.getTextures(`${meter.mode}_METER_IDLE_TEXTURE`)[0];
        meter.meter.visible = true;
        meter.spinAnimation.state.setAnimation(0, this.METER_ANIMATION.stop0, false);
        meter.text.visible = (meter.text.text.length > 0);
    }

    public spin():void {
        this._hasPlusLanded = false;
        this._currentRoundWin = 0;
        this._currentRoundWinText.text.text = "";
        if(this._totalMulti > PearlHarbor.model.dogfight.totalMult) {
            debugger;
        }
        if(this._fsContainer.visible) {
            this._fsMeters.forEach((meter:IMeter, i:number) => {
                this.spinMeter(meter);
            });
        }
    }

    public playMissMul():void {
        this._addEffects.visible = true;
        new SpineTween(this._addEffects, 0, "miss_total");
        PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_MISS_NUMBER);
        this._currentRoundWinText.text.text = "";
    }

    public showWin():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline(),
            spreadDelay = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0 : 1;
        const model:PearlHarborModel = PearlHarbor.model,
            winData:IDogFight | IFinalWave = model.dogfight;
        let spinningMeterCounter:number = 0,
            stopThisRoundCounter:number = 0,
            playSpread = false;
        this._hasPlusLanded = false;

        this._fsMeters.forEach((meter:IMeter, i:number) => {
            const x:number = Math.floor(i / 3),
                y:number = i % 3,
                isSFS:boolean = this.isSFS(),
                winValues:number[][] = isSFS ? (<IFinalWave>winData).columnsHitThisSpinBeforeSpread : winData.columns,
                value:number = winValues[x][y],
                isEmpty:boolean = !meter.hasWon;
            spinningMeterCounter += (isEmpty ? 1 : 0);
            if(isEmpty && value > 0) {
                stopThisRoundCounter++;
                if(isSFS && y === 1) {
                    if(!(this._fsMeters[i - 1].hasWon)) {
                        stopThisRoundCounter++;
                    }
                    tl.add(this.spreadWin(x, value, winData.mult[x][y], winData.mult[x][y - 1], spreadDelay), 0.1);
                    this._hasPlusLanded = (this._hasPlusLanded || (this._fsMeters[i - 1].isActive && !winData.mult[x][y - 1]));
                    playSpread = true;
                } else {
                    tl.add(this.updateMeterWin(meter, value, winData.mult[x][y]), 0.1);

                }
                this._hasPlusLanded = (this._hasPlusLanded || !winData.mult[x][y])
            }
            tl.add(() => this.stopMeter(meter, i), 0.1);
        });

        if(playSpread) {
            tl.add(() => {PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_SPREAD)}, spreadDelay + 0.1);
        }

        if(stopThisRoundCounter > 0) {
            tl.add(() => {
                this._bonusMeters.updateKamikazeMeter(18 - spinningMeterCounter + stopThisRoundCounter);
                PearlHarborSoundManager.playMeterHit(stopThisRoundCounter);
                PearlHarbor.backgroundElements.playCockpitHitAnimation(stopThisRoundCounter);
                PearlHarbor.backgroundElements.playCockpitDamageAnimation(18 - spinningMeterCounter + stopThisRoundCounter);
            }, 0.1);
        }

        if(PearlHarbor.model.dogfight.spinsLeft === 3) {
            tl.add(() => {
                this.updateSpinLeftCount(true);
                SlotGame.keypadPlugin.setZeroBetSpinCounter(PearlHarbor.model.dogfight.spinsLeft);
            }, 0.3);
        }

        if(tl.duration() < 0.5) {
            //add no win stop
            tl.add(() => {}, 0.5);
        }

        return tl;
    }

    public updateMultiplier(addValue:number, delay:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        if(addValue > 0) {
            tl.add(
                () => {
                    this._totalMulti += addValue;
                    this._currentRoundWin += PearlHarbor.model.dogfight.initialMultFromTrigger;
                    this._addEffects.visible = true;
                    new SpineTween(this._addEffects, 0, "add_to_total");
                    PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_ADD_TO_TOTAL);
                }, delay);
            tl.add([
                () => {this._currentRoundWinText.text.scale.set(1.2)},
                gsap.to(this._currentRoundWinText.text.scale, {duration : 0.2, x : 1, y : 1}),
                gsap.to(this._currentRoundWinText.text, {duration : 0.6, x : -600}),
                gsap.to(this._currentRoundWinText.text, {duration : 0.4, alpha : 0, delay : 0.1, ease : "power2.out"})
            ], delay + 0.4);
        }
        tl.add([
            () => {
                this._multiplier.text.text = "X" + (this._totalMulti <= 1 ? "" : this._totalMulti.toString());
                this._multiplier.text.scale.set(1.3, 1.3);
                if(addValue == 0) {
                    // end of fs
                    PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_TOTAL_MUL_ENDING);
                    this._addEffects.visible = true;
                    new SpineTween(this._addEffects, 0, "final_total");
                }
            },
            gsap.to(this._multiplier.text.scale, {duration : 0.2, x : 1, y : 1}),
            // gsap.to(this._multiplier.text, {duration : 0.01, pixi : {brightness : 3}})
        ], addValue > 0 ? delay + 0.6 : 0.1);
        tl.add(() => {
            this._currentRoundWinText.text.text = "";
            this._currentRoundWinText.text.alpha = 1;
            this._currentRoundWinText.text.x = 0;
        });

        return tl;
    }

    private updateMeterData(meter:IMeter, value:number, isMulti:boolean):void {
        if(isMulti) {
            meter.text.style.fontSize = 120;
            meter.text.style.letterSpacing = -14;
        }
        meter.meter.visible = true;
        meter.text.visible = true;
        meter.text.alpha = 0;
        meter.value += value;
        meter.text.text = `${isMulti ? "X" : "+"}${meter.value}`;
        meter.isMulti = isMulti;
        meter.hasWon = true;
    }

    private updateMeterWin(meter:IMeter, value:number, isMulti:boolean):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        this.updateMeterData(meter, value, isMulti);
        tl.add([() => {
            if(isMulti) {
                meter.spinAnimation.state.setAnimation(2, this.METER_ANIMATION.waiting, true);
            }
            meter.spinAnimation.state.setAnimation(5, this.METER_ANIMATION.cracked, false);
        },
            gsap.to(meter.text,  {duration:0.2, alpha : 1}),
            new SpineTween(meter.spinAnimation, 0, isMulti ? this.METER_ANIMATION.multiplierHit : this.METER_ANIMATION.plusHit)
        ]);

        return tl;
    }

    private updateMeterSpreadWin(meter:IMeter, value:number, isMulti:boolean):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add([
            () => {
                this.updateMeterData(meter, value, isMulti);
                if(isMulti && meter.isActive) {
                    meter.spinAnimation.state.setAnimation(2, this.METER_ANIMATION.waiting, true);
                }
                meter.spinAnimation.state.setAnimation(5, this.METER_ANIMATION.cracked, false);
                meter.spinAnimation.state.setAnimation(0, isMulti ? this.METER_ANIMATION.multiplierHit : this.METER_ANIMATION.plusHit, false);
                meter.text.alpha = 1;
                meter.text.scale.set(1.3);
            },
            gsap.to(meter.text.scale, {duration : 0.2, x : 1, y : 1})
        ]);

        return tl;
    }

    private showSpreadWin(meter:IMeter, value:number, isMulti:boolean, spreadDelay:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        this.updateMeterData(meter, value, isMulti);
        tl.add([
            () => {
                if(isMulti) {
                    meter.spinAnimation.state.setAnimation(2, this.METER_ANIMATION.waiting, true);
                }
                meter.spinAnimation.state.setAnimation(5, this.METER_ANIMATION.cracked, false);
                meter.spinAnimation.state.setAnimation(0, isMulti ? this.METER_ANIMATION.multiplierHit : this.METER_ANIMATION.plusHit, false);
            },
            gsap.to(meter.text,  {duration: 0.2, alpha : 1}),
        ]);
        tl.add(new SpineTween(meter.spinAnimation, 0, this.METER_ANIMATION.spread), spreadDelay);
        return tl;
    }

    private showBeingSpread(meter:IMeter, value:number, isMulti:boolean, spreadDelay:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(new SpineTween(meter.spinAnimation, 0, this.METER_ANIMATION.receiving), spreadDelay);
        tl.add(this.updateMeterSpreadWin(meter, value, isMulti));
        return tl;
    }

    private showFilledColumnWin(meter:IMeter):gsap.core.Timeline {
        const tl = gsap.timeline();

        tl.add(() => {meter.spinAnimation.state.setAnimation(4, this.METER_ANIMATION.columnWin, false)});
        tl.add(() => {
            if(meter.hasBurnt) {
                meter.spinAnimation.state.setAnimation(1, this.METER_ANIMATION.burningSpent, true)
            } else {
                meter.spinAnimation.state.setAnimation(1, this.METER_ANIMATION.burning, true)
            }
        });

        return tl;
    }

    private showFilledRowWin(meter:IMeter):gsap.core.Timeline {
        return gsap.timeline().add(new SpineTween(meter.spinAnimation, 3, this.METER_ANIMATION.rowWin))
            .add(() => {
                if(meter.hasBurnt) {
                    meter.spinAnimation.state.setAnimation(1, this.METER_ANIMATION.burningSpent, true)
                } else {
                    meter.spinAnimation.state.setAnimation(1, this.METER_ANIMATION.burning, true)
                }
            });
    }

    public fillColumnWin(i:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        let sum:number = 0,
            multi:number = 1;
        const columnData:number[][] = PearlHarbor.model.dogfight.columns,
            isMultis:boolean[][] = PearlHarbor.model.dogfight.mult;
        for(let j:number = 0; j < 3; j++) {
            sum += isMultis[i][j] ? 0 : columnData[i][j];
            multi *= isMultis[i][j] ? columnData[i][j] : 1;
        }
        this._columnSums[i] = (sum === 0 ? 1 : sum) * multi;
        tl.add([
            this.showFilledColumnWin(this._fsMeters[i * 3]),
            this.showFilledColumnWin(this._fsMeters[i * 3 + 1]),
            this.showFilledColumnWin(this._fsMeters[i * 3 + 2]),
            () => {
                PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_COLUMN_ON);
                this._bonusMeters.updateMGProgress(true);
            }
        ]);
        return tl;
    }

    public spreadWin(i:number, value:number, isMulti:boolean, isSpreadMulti:boolean, spreadDelay:number):gsap.core.Timeline {
        if(!this._sfsContainer.visible) {
            debugger;
        }
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add([
            this.showSpreadWin(this._fsMeters[i * 3 + 1], value, isMulti, spreadDelay),
            this.showBeingSpread(this._fsMeters[i * 3], value, isSpreadMulti, spreadDelay)
        ]);

        tl.add(() => {
        }, 0.1);
        return tl;
    }

    public fillRowWin(i:number):gsap.core.Timeline {
        if(!this._sfsContainer.visible) {
            debugger;
        }

        const tl:gsap.core.Timeline = gsap.timeline();
        let sum:number = 0,
            multi:number = 1;
        const columnData:number[][] = PearlHarbor.model.dogfight.columns,
            isMultis:boolean[][] = PearlHarbor.model.dogfight.mult;

        for(let j:number = 0; j < 6; j++) {
            tl.add(this.showFilledRowWin(this._fsMeters[j * 3 + i]), 0);
            sum += isMultis[j][i] ? 0 : columnData[j][i];
            multi *= isMultis[j][i] ? columnData[j][i] : 1;
        }
        tl.add(() => {PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_ROW_ON);}, 0)
        tl.add(() => {this._bonusMeters.updateBAProgress(true);})
        this._rowSums[i] = (sum === 0 ? 1 : sum) * multi;
        tl.add(() => {
        }, 0.1);
        return tl;
    }

    public setMeterCracked(meter:IMeter):void {
        meter.swooshHolder.visible = false;
        meter.spinAnimation.state.setAnimation(5, this.METER_ANIMATION.cracked, false);
        if(meter.isMulti) {
            meter.spinAnimation.state.clearTrack(2);
        }
        this.setTextStyle(meter);
    }

    public updateAllMetersWin():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        const plusDelay = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0 : 1,
            mulDelay = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0 : 1;
        let sum:number = 0,
            multi:number = this._mainGameMulti.visible ? PearlHarbor.model.dogfight.initialMultFromTrigger : 1,
            animateDelay:number = this.WIN_NUMBER_FLY_TO_MEDDLE;

        const isLastRound:boolean = PearlHarbor.model.isNextNormal && !PearlHarbor.model.isNormal;

        // last round change the X to + if any.
        if(!this._hasPlusLanded) {
            if(isLastRound && !(PearlHarbor.model.gameData.brokeBank && PearlHarbor.model.dogfight.integerRepresentingWhenBreakBank < 15)) {
                if(this._mainGameMulti.visible) {
                    this._mainGameMulti.text = "+" + multi.toString();
                    sum += multi;
                }
                let swooshCounter = 0;
                this._fsMeters.forEach((meter:IMeter) => {
                    if(meter.hasWon && meter.isActive && meter.isMulti) {
                        swooshCounter++;
                        meter.text.text = "+" + meter.value.toString();
                        sum += meter.value;
                        meter.isActive = false;
                        const path = this.getPathData(this.VALUE_COLLECTOR_POS[0] - meter.meter.position.x, this.VALUE_COLLECTOR_POS[1] - meter.meter.position.y)
                        tl.add([
                            () => {
                                meter.flyText.style.fill = this.FS_METER_STATE_COLOR[0];
                                meter.flyText.text = meter.text.text;
                                meter.flyText.alpha = 0;
                                meter.swooshHolder.visible = true;
                                this.playSwoosh(meter.swooshContainer, path, false);
                            },
                            gsap.to(meter.flyText, {
                                duration : animateDelay,
                                ease : "power2.out",
                                pixi:{
                                    motionPath : {
                                        path : path,
                                        type : "cubic",
                                        alignOrigin : [0.5, 0.5]
                                    }
                                }
                            }),
                            gsap.to(meter.flyText,
                                {duration : animateDelay - 0.3, alpha : 0, ease : "power4.in"}
                            )
                        ], plusDelay);
                        tl.add(() => {
                            this._currentRoundWin = sum;
                            this._currentRoundWinText.text.style.fill = this.FS_METER_STATE_COLOR[0];
                            this._currentRoundWinText.text.text = this._currentRoundWin.toString();
                            this._currentRoundWinText.text.scale.set(1.1, 1.1);
                            gsap.to(this._currentRoundWinText.text.scale, {duration : 0.1, x : 1, y : 1});
                            this._addEffects.visible = true;
                            new SpineTween(this._addEffects, 0, "plus_total");
                            PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_ROUND_NUMBER_ADD);
                        }, plusDelay + animateDelay);
                        tl.add(() => {
                            this.setMeterCracked(meter);
                        }, plusDelay + animateDelay);
                    }
                });
                if(swooshCounter > 0) {
                    tl.add(() => {PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_SWOOSH);}, plusDelay);
                }

                if(this._mainGameMulti.visible) {
                    const mainMultiPath = this.getPathData(this.VALUE_COLLECTOR_POS[0], this.VALUE_COLLECTOR_POS[1], this._mainGameMulti.x, this._mainGameMulti.y);
                    tl.add([
                        () => {
                            this._mainGameMulti.alpha = 1;
                            this.playSwoosh(this._mainMultiSwooshContainer, mainMultiPath, true);
                        },
                        gsap.to(this._mainGameMulti, {
                            duration : animateDelay,
                            ease : "power2.out",
                            pixi:{
                                motionPath:{
                                    path:mainMultiPath,
                                    type:"cubic",
                                    alignOrigin:[0.5, 0.5]
                                }
                            }
                        }),
                        gsap.to(this._mainGameMulti,
                            {duration : animateDelay - 0.3, alpha : 0, ease : "power4.in"}
                        )
                    ], plusDelay);
                    tl.add(() => {
                        this._mainGameMulti.visible = false;
                        this._mainGameMulti.alpha = 1;
                    }, plusDelay + this.WIN_NUMBER_SHOW_IN_MEDDLE);
                }
                tl.add(this.updateMultiplier(sum, SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0 : 1), animateDelay + this.WIN_NUMBER_SHOW_IN_MEDDLE);
                tl.add(() => {}, this.WIN_NUMBER_SHOW_IN_MEDDLE);
            }
            return tl;
        }

        let multiId:number = 0, plusId:number = 0;
        this._fsMeters.forEach((meter:IMeter) => {
            if(meter.hasWon && meter.isActive) {
                sum += meter.isMulti ? 0 : meter.value;
                multi *= meter.isMulti ? meter.value : 1;
                multiId += (meter.isMulti ? 1 : 0);
                plusId += (meter.isMulti ? 0 : 1);
                meter.isActive = false;
                const path = this.getPathData(this.VALUE_COLLECTOR_POS[0] - meter.meter.position.x, this.VALUE_COLLECTOR_POS[1] - meter.meter.position.y)
                tl.add([
                    () => {
                        meter.flyText.style.fill = this.FS_METER_STATE_COLOR[0];
                        meter.flyText.text = meter.text.text;
                        meter.flyText.alpha = 0;
                        meter.swooshHolder.visible = true;
                        this.playSwoosh(meter.swooshContainer, path, meter.isMulti);
                    },
                    gsap.to(meter.flyText, {
                        duration : animateDelay,
                        ease : "power2.out",
                        pixi:{
                            motionPath:{
                                path:path,
                                type:"cubic",
                                alignOrigin:[0.5, 0.5]
                            }
                        }
                    }),
                    gsap.to(meter.flyText,
                        {duration : animateDelay - 0.3, alpha : 0, ease : "power4.in"}
                    ),
                    new SpineTween(meter.spinAnimation, 0, this.METER_ANIMATION.payout)
                ], plusDelay + (meter.isMulti ? (mulDelay + (animateDelay + this.WIN_NUMBER_SHOW_IN_MEDDLE) * multiId) : 0));
                tl.add([
                    () => {
                        if(meter.isMulti) {
                            this._currentRoundWin *= meter.value;
                            this._currentRoundWinText.text.style.fill = this.FS_METER_STATE_COLOR[0];
                            this._currentRoundWinText.text.text = this._currentRoundWin.toString();
                            this._currentRoundWinText.text.scale.set(1.1, 1.1);
                            gsap.to(this._currentRoundWinText.text.scale, {
                                duration : 0.1,
                                x : 1,
                                y : 1
                            });
                            this._addEffects.visible = true;
                            new SpineTween(this._addEffects, 0, "multiplier_total");
                            PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_ROUND_NUMBER_ADD);
                        }
                        this.setMeterCracked(meter);
                    }
                ], plusDelay + (meter.isMulti ? (mulDelay + (animateDelay + this.WIN_NUMBER_SHOW_IN_MEDDLE) * multiId) : 0) + animateDelay);
            }
        });
        tl.add(() => {PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_SWOOSH);}, plusDelay); // all add swoosh play one sound.

        if(plusId > 0) {
            tl.add(() => {
                this._currentRoundWin = sum;
                this._currentRoundWinText.text.style.fill = this.FS_METER_STATE_COLOR[0];
                this._currentRoundWinText.text.text = this._currentRoundWin.toString();
                this._currentRoundWinText.text.scale.set(1.1, 1.1);
                gsap.to(this._currentRoundWinText.text.scale, {duration : 0.1, x : 1, y : 1});
                this._addEffects.visible = true;
                new SpineTween(this._addEffects, 0, "plus_total");
                PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_ROUND_NUMBER_ADD);
            }, plusDelay + animateDelay);
        }

        if(this._mainGameMulti.visible) {
            multiId++;
            const mainMultiPath = this.getPathData(this.VALUE_COLLECTOR_POS[0], this.VALUE_COLLECTOR_POS[1], this._mainGameMulti.x, this._mainGameMulti.y);
            tl.add([
                () => {
                    this._mainGameMulti.alpha = 1;
                    this.playSwoosh(this._mainMultiSwooshContainer, mainMultiPath, true);
                },
                gsap.to(this._mainGameMulti, {
                    duration : animateDelay,
                    ease : "power2.out",
                    pixi:{
                        motionPath:{
                            path:mainMultiPath,
                            type:"cubic",
                            alignOrigin:[0.5, 0.5]
                        }
                    }
                }),
                gsap.to(this._mainGameMulti,
                    {duration : animateDelay - 0.3, alpha : 0, ease : "power4.in"}
                )
            ], plusDelay + mulDelay + (animateDelay + this.WIN_NUMBER_SHOW_IN_MEDDLE) * multiId);
            tl.add(
                () => {
                    this._currentRoundWin *= PearlHarbor.model.dogfight.initialMultFromTrigger;
                    this._currentRoundWinText.text.style.fill = this.FS_METER_STATE_COLOR[0];
                    this._currentRoundWinText.text.text = this._currentRoundWin.toString();
                    this._currentRoundWinText.text.scale.set(1.2, 1.2);
                    gsap.to(this._currentRoundWinText.text.scale, {duration : 0.1, x : 1, y : 1});
                    this._addEffects.visible = true;
                    new SpineTween(this._addEffects, 0, "multiplier_total");
                    PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_ROUND_NUMBER_ADD);
                    this._mainGameMulti.visible = false;
                    this._mainGameMulti.alpha = 1;
                }, plusDelay + mulDelay + (animateDelay + this.WIN_NUMBER_SHOW_IN_MEDDLE) * multiId + animateDelay);
            multiId++;
        }

        tl.add(this.updateMultiplier(sum * multi, SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0 : 1));
        tl.add(() => {}, this.WIN_NUMBER_SHOW_IN_MEDDLE); // for if there is noting in the last spin, give it a pause.
        return tl;
    }

    public addRowWinInMiddle(row:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        const animateDelay = this.WIN_NUMBER_FLY_TO_MEDDLE,
            rowDelay = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0 : 1;
        this._fsMeters.forEach((meter:IMeter, i:number) => {
            if(i % 3 === row) {
                const path = this.getPathData(this.VALUE_COLLECTOR_POS[0] - meter.meter.position.x, this.VALUE_COLLECTOR_POS[1] - meter.meter.position.y);
                tl.add([
                    () => {
                        meter.flyText.style.fill = this.FS_METER_STATE_COLOR[1];
                        meter.flyText.text = meter.text.text;
                        meter.flyText.position.set(0,0);
                        meter.flyText.alpha = 0;
                        meter.swooshHolder.visible = true;
                        this.playSwoosh(meter.swooshContainer, path, false);
                    },
                    gsap.to(meter.flyText,
                        {
                            duration : animateDelay,
                            ease : "power2.out",
                            pixi:{
                                motionPath:{
                                    path:path,
                                    type:"cubic",
                                    alignOrigin:[0.5, 0.5]
                                }
                            }
                        }),
                    gsap.to(meter.flyText,
                        {duration : animateDelay - 0.3, alpha : 0, ease : "power4.in"}
                    )
                ], rowDelay);
                tl.add(() => {
                    this.setTextStyle(meter, true);
                    meter.hasBurnt2 = meter.hasBurnt;
                    meter.hasBurnt = true;
                });
            }
        });
        tl.add(() => {PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_SWOOSH);}, rowDelay);
        const addValue:number = this.rowSums[row];
        tl.add(() => {
            this._currentRoundWinText.text.style.fill = this.FS_METER_STATE_COLOR[1];
            this._currentRoundWinText.text.text = addValue.toString();
            this._currentRoundWinText.text.scale.set(1.2, 1.2);
            this._addEffects.visible = true;
        });
        tl.add([
            gsap.to(this._currentRoundWinText.text.scale, {duration : 0.1, x : 1, y : 1}),
            new SpineTween(this._addEffects, 0, "row_total"),
            () => {PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_ROUND_NUMBER_ADD);}
        ]);
        if(!SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
            tl.add(() => {}, `+=1`);
        }
        return tl;
    }

    public afterRowWin(row:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        this._fsMeters.forEach((meter:IMeter, i:number) => {
            if(i % 3 === row) {
                this._rowHit[row] = 0;
                tl.add([
                    new SpineTween(meter.spinAnimation, 0, this.METER_ANIMATION.payout),
                    () => {
                        meter.swooshHolder.visible = false;
                    },
                ], 0);
            }
        });

        return tl;
    }

    public afterColumnWin(column:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        this._fsMeters.forEach((meter:IMeter, i:number) => {
            if(Math.floor(i / 3) === column) {
                this._columnHits[column] = 0;
                tl.add([
                    new SpineTween(meter.spinAnimation, 0, this.METER_ANIMATION.payout),
                    () => {
                        meter.swooshHolder.visible = false;
                    },
                ], 0);
            }
        });

        return tl;
    }

    public addColumnWinInMiddle(column:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        const animateDelay = this.WIN_NUMBER_FLY_TO_MEDDLE,
            columnDelay = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0 : 1;
        this._fsMeters.forEach((meter:IMeter, i:number) => {
            if(Math.floor(i / 3) === column) {
                const path = this.getPathData(this.VALUE_COLLECTOR_POS[0] - meter.meter.position.x, this.VALUE_COLLECTOR_POS[1] - meter.meter.position.y)
                tl.add([
                    () => {
                        meter.flyText.style.fill = this.FS_METER_STATE_COLOR[1];
                        meter.flyText.text = meter.text.text;
                        meter.flyText.position.set(0,0);
                        meter.flyText.alpha = 0;
                        meter.swooshHolder.visible = true;
                        this.playSwoosh(meter.swooshContainer, path, false);
                    },
                    gsap.to(meter.flyText,
                        {
                            duration : animateDelay,
                            ease : "power2.out",
                            pixi:{
                                motionPath:{
                                    path:path,
                                    type:"cubic",
                                    alignOrigin:[0.5, 0.5]
                                }
                            }
                        }),
                    gsap.to(meter.flyText,
                        {duration : animateDelay - 0.3, alpha : 0, ease : "power4.in"}
                    )
                ], columnDelay);
                tl.add(() => {
                    this.setTextStyle(meter, true);
                    meter.hasBurnt2 = meter.hasBurnt;
                    meter.hasBurnt = true;
                });
            }
        });
        tl.add(() => {PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_SWOOSH);}, columnDelay);
        const addValue:number = this.columnSums[column];
        tl.add(() => {
            this._currentRoundWinText.text.style.fill = this.FS_METER_STATE_COLOR[1];
            this._currentRoundWinText.text.text = addValue.toString();
            this._currentRoundWinText.text.scale.set(1.2, 1.2);
            gsap.to(this._currentRoundWinText.text.scale, {duration : 0.1, x : 1, y : 1});
            this._addEffects.visible = true;
            new SpineTween(this._addEffects, 0, "column_total");
            PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_ROUND_NUMBER_ADD);
        })

        return tl;
    }

    public updateCurrentRoundWin(lastWin:boolean = false):void {
        const tl = gsap.timeline();

        tl.add(() => {
            let total:number = PearlHarbor.model.dogfight.totalMult / (PearlHarbor.model.dogfight.kamakazi ? PearlHarbor.model.dogfight.kamikaziMult : 1);
            total = Math.min(total, 41127);
            if(lastWin && (PearlHarbor.model.dogfight.integerRepresentingWhenBreakBank != 15) && total != this._totalMulti) {
                debugger;
                this._multiplier.text.text = total.toString();
            }

        });

    }

    public addMainGameMulti():void {
        if(PearlHarbor.model.mainGameMultiHasBeenUsed) {
            return;
        }
        const multi:number = PearlHarbor.model.dogfight.initialMultFromTrigger;
        this._mainGameMulti.visible = multi > 1;
        this._mainGameMulti.text = "X" + multi.toString();
    }

    public updateSpinLeftCount(fromWin:boolean = false):void {
        this._bonusMeters.updateFSLeft((PearlHarbor.model.dogfight.spinsLeft - (fromWin ? 0 : 1)));
    }

    public show(isRestore:boolean):void {
        const isSFS:boolean = (PearlHarbor.model.data.nextMode === PearlHarborGameMode.SUPER_FREESPIN),
            data:IFinalWave | IDogFight = PearlHarbor.model.dogfight;
        this._fsMeters = this.addMeters(this.FS_METER_POS, this._fsContainer, isSFS);
        this._totalMulti = (PearlHarbor.model.dogfight!.totalMult ? PearlHarbor.model.dogfight.totalMult : 0);
        this._rowSums = [0, 0, 0];
        this._columnSums = [0, 0, 0, 0, 0, 0];
        this._rowHit = ArrayHelper.initArrayWithValues(3, () => 0);
        this._columnHits = ArrayHelper.initArrayWithValues(6, () => 0);
        this._multiplier.text.text = "X" + (this._totalMulti <= 1 ? "" : this._totalMulti.toString());
        this._swooshContainer.visible = this._fsContainer.visible = !PearlHarbor.model.isNextNormal;
        this._sfsContainer.visible = isSFS;

        if(isRestore) {
            data.columns.forEach((column:number[], i) => {
                column.forEach((value:number, j:number) => {
                    if(value > 0) {
                        const isMulti:boolean = PearlHarbor.model.dogfight.mult[i][j];
                        this.updateMeterData(this._fsMeters[i * 3 + j], value, isMulti);
                        this._fsMeters[i * 3 + j].text.alpha = 1;
                        this._fsMeters[i * 3 + j].spinAnimation.state.setAnimation(5, this.METER_ANIMATION.cracked, false);
                        this._fsMeters[i * 3 + j].spinAnimation.state.setAnimation(0, isMulti ? this.METER_ANIMATION.multiplierHit : this.METER_ANIMATION.plusHit, false);
                        if(!isMulti) {
                            this._fsMeters[i * 3 + j].text.style.fill = this.FS_METER_STATE_COLOR[1];
                            this._fsMeters[i * 3 + j].isActive = false;
                        } else if(PearlHarbor.model.dogfight.activePositionsWithMults.indexOf(i * 3 + j) > -1) {
                            this._fsMeters[i * 3 + j].text.style.fill = this.FS_METER_STATE_COLOR[0];
                            this._fsMeters[i * 3 + j].isActive = true;
                            this._fsMeters[i * 3 + j].spinAnimation.state.setAnimation(2, this.METER_ANIMATION.waiting, true);
                        } else {
                            this._fsMeters[i * 3 + j].text.style.fill = this.FS_METER_STATE_COLOR[1];
                            this._fsMeters[i * 3 + j].isActive = false;
                        }
                    }
                });
            });
            this._fsMeters.forEach((meter:IMeter, s:number) => {
                if(data.filledColumns[Math.floor(s / 3)] && meter.hasWon) {
                    this.setTextStyle(meter, true);
                    meter.hasBurnt2 = meter.hasBurnt;
                    meter.hasBurnt = true;
                    meter.spinAnimation.state.setAnimation(1, this.METER_ANIMATION.burning, true);
                }
                if(isSFS) {
                    if((<IFinalWave>data).filledRows[s % 3] && meter.hasWon) {
                        this.setTextStyle(meter, true);
                        meter.spinAnimation.state.setAnimation(1, meter.hasBurnt ? this.METER_ANIMATION.burningSpent : this.METER_ANIMATION.burning, true);
                        meter.hasBurnt2 = meter.hasBurnt;
                        meter.hasBurnt = true;
                    }
                }

            });
        }
        this._bonusMeters.updateMode(!isSFS, isRestore);
        this.visible = true;
    }

    private getPathData(toX:number, toY:number, fromX?:number, fromY?:number):PIXI.IPointData[] {
        if(!fromX) {
            fromX = 0;
        }
        if(!fromY) {
            fromY = 0;
        }
        return [{x : fromX, y : fromY}, {x : fromX, y : toY}, {x : toX * 0.8, y : toY}, {x : toX, y : toY}];
    }

    public playSwoosh(container:PIXI.Container, pointsData:PIXI.IPointData[], playSound:boolean = false):void {
        const swoosh = new AnimatedMeshRope(PearlHarbor.assetLoader.getTextures(`effects/fs_win_swoosh/fs_win_swoosh_`),
            pointsData);
        swoosh.blendMode = PIXI.BLEND_MODES.ADD;
        container.addChild(swoosh);
        swoosh.playAnimation();
        if(playSound) {
            PearlHarborSoundManager.playSoundEffect(PearlHarborSoundConfig.FS_SWOOSH);
        }
    }

    public addAttention():void {
        if(this._fsContainer.visible) {
            const isSFS:boolean = this.isSFS();
            this._fsMeters.forEach((meter:IMeter, j:number) => {
                if(isSFS && this._rowHit[j % 3] === 5) {
                    meter.spinAnimation.state.setAnimation(3, this.METER_ANIMATION.rowAttention, true);
                }
                if(this._columnHits[Math.floor(j / 3)] === 2) {
                    meter.spinAnimation.state.setAnimation(4, this.METER_ANIMATION.columnAttention, true);
                }
            });
        }
    }

    public updateBonusMeter():void {
        if(this._fsContainer.visible) {
            let dogfight:IFinalWave | IDogFight = PearlHarbor.model.dogfight,
                rowAttention:number[] = ArrayHelper.initArrayWithValues(3, () => 0),
                columnAttention:number[] = ArrayHelper.initArrayWithValues(6, () => 0),
                isSFS:boolean = this.isSFS();
            this._fsMeters.forEach((meter:IMeter, i:number) => {
                if(isSFS) {
                    const filledRowNotThisTurn:boolean = ((<IFinalWave>dogfight).filledRows[i % 3] && !(<IFinalWave>dogfight).filledRowThisSpin[i % 3]);
                    rowAttention[i % 3] += (filledRowNotThisTurn ? 0 : ((meter.hasWon) ? 1 : 0));
                }
                const filledNotThisTurn:boolean = (dogfight.filledColumns[Math.floor(i / 3)] && !dogfight.filledColumnThisSpin[Math.floor(i / 3)]);
                columnAttention[Math.floor(i / 3)] += (filledNotThisTurn ? 0 : (meter.hasWon ? 1 : 0));
            });
            this._bonusMeters.updateMGMeter(Math.max.apply(Math, columnAttention));
            if(isSFS) {this._bonusMeters.updateBAMeter(Math.max.apply(Math, rowAttention));}
            this._columnHits = columnAttention;
            this._rowHit = rowAttention;
        }
    }

    public updateMGProgress():gsap.core.Timeline {
        return gsap.timeline().add([
            () => {this._bonusMeters.updateMGMeter(Math.max.apply(Math, this._columnHits));},
            this._bonusMeters.updateMGProgress()
        ]);
    }

    public updateBAProgress():gsap.core.Timeline {
        return gsap.timeline().add([
            () => {this._bonusMeters.updateBAMeter(Math.max.apply(Math, this._rowHit));},
            this._bonusMeters.updateBAProgress()
        ]);
    }

    public get columnSums():number[] {
        return this._columnSums;
    }

    public get rowSums():number[] {
        return this._rowSums;
    }

    public get columnHits():number[] {
        return this._columnHits;
    }

    public get rowHits():number[] {
        return this._rowHit;
    }

    public get totalMulti():number {
        return this._totalMulti;
    }

    public set totalMulti(value:number) {
        this._totalMulti = value;
    }
}